import {
  Box,
  Typography,
  Button,
  Container,
} from '@mui/material';
import {
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useRef, useState } from 'react';

interface StudentData {
  _id: string;
  candidateName: string;
  registrationNo: string;
  course: string;
  session: string;
  year: string;
  faculty: string;
  stream: string;
  contactNumber: string;
  emailAddress: string;
  dateOfBirth?: string;
  photo?: string;
}

const IdCardGenerationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const idCardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // Using fixed pixel positions like admit card - no responsive positioning needed
  
  // Get student data from navigation state or use default values
  const studentData: StudentData = location.state?.studentData || {
    _id: '',
    candidateName: 'John Doe',
    registrationNo: '123456789',
    course: 'B.Tech',
    session: '2025',
    year: '2025',
    faculty: 'Engineering',
    stream: 'Computer Science',
    contactNumber: '9876543210',
    emailAddress: 'john.doe@example.com',
    dateOfBirth: '2000-01-01',
    photo: undefined
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownload = async () => {
    if (idCardRef.current) {
      setIsDownloading(true);
      try {
        // Wait for all images to load
        const images = idCardRef.current.querySelectorAll('img');
        
        await Promise.all(
          Array.from(images).map((img) => {
            return new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve(true);
              } else {
                img.onload = () => resolve(true);
                img.onerror = () => resolve(true);
              }
            });
          })
        );
        
        // Additional wait to ensure everything is rendered
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get exact dimensions from the element (without padding/margins)
        const rect = idCardRef.current.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;
        
        // Use maximum scale for best quality (5x for ultra-high quality)
        const scale = 5;
        const dpi = 300;
        
        // Capture with exact dimensions - no windowWidth/windowHeight to avoid distortion
        const canvas = await html2canvas(idCardRef.current, {
          backgroundColor: '#ffffff',
          scale: scale,
          useCORS: true,
          allowTaint: true,
          logging: false,
          width: width,
          height: height,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: (clonedDoc) => {
            // Ensure all fonts are loaded
            const clonedWindow = clonedDoc.defaultView;
            if (clonedWindow) {
              clonedWindow.document.fonts.ready;
            }
            // Ensure SVG images are rendered at high resolution
            const clonedImages = clonedDoc.querySelectorAll('img');
            clonedImages.forEach((img) => {
              if (img.src.endsWith('.svg')) {
                (img as HTMLImageElement).style.imageRendering = 'auto';
              }
            });
          },
        });
        
        // Crop canvas to remove white space on all sides
        let finalWidth = width;
        let finalHeight = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;
          
          // Find content bounds (top, left, bottom, right)
          let top = 0, left = 0, bottom = canvas.height, right = canvas.width;
          
          // Find top (first row with non-white content)
          for (let y = 0; y < canvas.height; y++) {
            let hasContent = false;
            for (let x = 0; x < canvas.width; x++) {
              const idx = (y * canvas.width + x) * 4;
              const r = pixels[idx];
              const g = pixels[idx + 1];
              const b = pixels[idx + 2];
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              top = Math.max(0, y - 5);
              break;
            }
          }
          
          // Find bottom (last row with non-white content)
          for (let y = canvas.height - 1; y >= 0; y--) {
            let hasContent = false;
            for (let x = 0; x < canvas.width; x++) {
              const idx = (y * canvas.width + x) * 4;
              const r = pixels[idx];
              const g = pixels[idx + 1];
              const b = pixels[idx + 2];
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              bottom = Math.min(canvas.height, y + 5);
              break;
            }
          }
          
          // Find left (first column with non-white content)
          for (let x = 0; x < canvas.width; x++) {
            let hasContent = false;
            for (let y = top; y < bottom; y++) {
              const idx = (y * canvas.width + x) * 4;
              const r = pixels[idx];
              const g = pixels[idx + 1];
              const b = pixels[idx + 2];
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              left = Math.max(0, x - 5);
              break;
            }
          }
          
          // Find right (last column with non-white content)
          for (let x = canvas.width - 1; x >= 0; x--) {
            let hasContent = false;
            for (let y = top; y < bottom; y++) {
              const idx = (y * canvas.width + x) * 4;
              const r = pixels[idx];
              const g = pixels[idx + 1];
              const b = pixels[idx + 2];
              if (r < 250 || g < 250 || b < 250) {
                hasContent = true;
                break;
              }
            }
            if (hasContent) {
              right = Math.min(canvas.width, x + 5);
              break;
            }
          }
          
          // Crop the canvas if needed
          if (left > 0 || top > 0 || right < canvas.width || bottom < canvas.height) {
            const croppedCanvas = document.createElement('canvas');
            const croppedWidth = right - left;
            const croppedHeight = bottom - top;
            croppedCanvas.width = croppedWidth;
            croppedCanvas.height = croppedHeight;
            const croppedCtx = croppedCanvas.getContext('2d');
            if (croppedCtx) {
              croppedCtx.drawImage(canvas, left, top, croppedWidth, croppedHeight, 0, 0, croppedWidth, croppedHeight);
              // Update dimensions for PDF (convert from scaled pixels to original size)
              finalWidth = croppedWidth / scale;
              finalHeight = croppedHeight / scale;
              // Replace canvas with cropped version
              canvas.width = croppedWidth;
              canvas.height = croppedHeight;
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(croppedCanvas, 0, 0);
            }
          }
        }
        
        // Use maximum quality JPEG (0.99 for near-lossless quality, much smaller than PNG)
        const imgData = canvas.toDataURL('image/jpeg', 0.99);
        
        // Calculate PDF dimensions in mm at target DPI
        const pdfWidth = (finalWidth / dpi) * 25.4;
        const pdfHeight = (finalHeight / dpi) * 25.4;
        
        // Create PDF with exact dimensions
        const pdf = new jsPDF({
          orientation: finalWidth > finalHeight ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight],
          compress: true,
        });
        
        // Add image at exact position (0, 0) to match preview
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        
        const fileName = `student-id-card-${studentData.candidateName.replace(/\s+/g, '-')}.pdf`;
        pdf.save(fileName);
      } catch (error) {
        console.error('Error generating ID card PDF:', error);
        alert('Error generating ID card PDF. Please try again.');
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Student ID Card
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Student ID Card for MIVPSA Institute
        </Typography>
      </Box>

          {/* Student ID Card with SVG Background */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Box
              ref={idCardRef}
              sx={{
                position: 'relative',
                width: '401px',
                minWidth: '401px',
                maxWidth: '401px',
                height: '700px',
                borderRadius: 2,
                boxShadow: 3,
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
                margin: 0,
                padding: 0,
                boxSizing: 'border-box',
              }}
            >
              {/* SVG Background */}
              <Box
                component="img"
                src="/student-id-card.svg"
                alt="Student ID Card"
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'cover',
                }}
              />
              
              {/* Overlay Student Data - No padding to ensure consistent positioning */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  color: 'black',
                }}
              >
                {/* Student Photo - Positioned in the photo box on the right */}
                <Box sx={{ 
                  position: 'absolute', 
                  right: '126px', 
                  top: '193px', 
                  width: '145px', 
                  height: '155px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                }}>
                  <Box
                    component="img"
                    src={studentData.photo}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      backgroundColor: '#f5f5f5',
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div style="
                            width: 100%; 
                            height: 100%; 
                            background-color: #f5f5f5; 
                            display: flex; 
                            align-items: center; 
                            justify-content: center;
                          ">
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="#999">
                              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                          </div>
                        `;
                      }
                    }}
                  />
                </Box>

                {/* Student Name - Positioned directly without nested containers */}
                <Typography 
                  variant="h6" 
                  sx={{ 
                    position: 'absolute',
                    left: '171px', // Combined: 23px + 145px
                    top: '372.6px', // Combined: 153.1px + 216.5px
                    fontWeight: 'bold', 
                    fontSize: '1.2rem',
                    color: 'black',
                    width: '200px',
                    margin: 0,
                    padding: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {studentData.candidateName}
                </Typography>

                {/* Roll No - Positioned directly */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    position: 'absolute',
                    left: '105px', // Combined: 17px + 83px
                    top: '405px', // Combined: 172px + 232px
                    fontWeight: 'bold', 
                    fontSize: '1.15rem',
                    color: 'black',
                    width: '200px',
                    margin: 0,
                    padding: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {studentData.registrationNo}
                </Typography>

                {/* Session - Positioned directly */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    position: 'absolute',
                    left: '96px', // Combined: 27px + 64px
                    top: '462px', // Combined: 217.5px + 243.5px
                    fontWeight: 'bold', 
                    fontSize: '1rem',
                    color: 'black',
                    width: '200px',
                    margin: 0,
                    padding: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {studentData.session}
                </Typography>

                {/* Course - Positioned directly */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    position: 'absolute',
                    left: '94px', // Combined: 16px + 72px
                    top: '490px', // Combined: 231.3px + 260px
                    fontWeight: 'bold', 
                    fontSize: '1rem',
                    color: 'black',
                    width: '180px',
                    maxHeight: '40px',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    margin: 0,
                    padding: 0,
                  }}
                >
                  {studentData.course}
                </Typography>

                {/* DOB - Positioned directly */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    position: 'absolute',
                    left: '69px', // Combined: 18px + 44px
                    top: '564.5px', // Combined: 282px + 283.5px
                    fontWeight: 'bold', 
                    fontSize: '1rem',
                    color: 'black',
                    width: '200px',
                    margin: 0,
                    padding: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {studentData.dateOfBirth 
                    ? new Date(studentData.dateOfBirth).toLocaleDateString('en-GB')
                    : 'N/A'
                  }
                </Typography>

                {/* Phone - Positioned directly */}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    position: 'absolute',
                    left: '86px', // Combined: 18px + 62px
                    top: '590px', // Combined: 304px + 287px
                    fontWeight: 'bold', 
                    fontSize: '1rem',
                    color: 'black',
                    width: '200px',
                    margin: 0,
                    padding: 0,
                    lineHeight: 1.2,
                  }}
                >
                  {studentData.contactNumber}
                </Typography>
              </Box>
            </Box>
          </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleDownload}
          disabled={isDownloading}
          sx={{ textTransform: 'none' }}
        >
          {isDownloading ? 'Generating PDF...' : 'Download ID Card (PDF)'}
        </Button>
      </Box>
    </Container>
  );
};

export default IdCardGenerationPage;
